import { useUserStore } from '~/store/common/user'
import {
  EXCHANGE_POLICY_HELP_CONTENT_ID_PRODUCTION,
  EXCHANGE_POLICY_HELP_CONTENT_ID_TEST,
  WARRANTY_POLICY_HELP_CONTENT_ID_PRODUCTION,
  WARRANTY_POLICY_HELP_CONTENT_ID_TEST,
} from '~/utils/constant'
import { resolveApplicableProductModels } from '~/utils/website/helpProductModels'
import { normalizeContentResource, resolveContentCoverUrl } from '@/utils/contentResource'
import { serializeServiceContentFilterValues } from '~/utils/website/serviceContentSearch'
import api from '../axios'

/**
 * @typedef {Object} UserOpinionDTO
 * @property {string} [userId] - 用户id
 * @property {number} [userType] - 用户角色 1 SMB 2工程商
 * @property {string} [opinionType] - 反馈类型
 * @property {string} [opinionDetail] - 反馈详情
 * @property {Array<string>} [pic] - 图片（最多 5 张）
 */

/**
 * @typedef {Object} SubmitFeedbackParams
 * @description 为兼容旧调用入参，允许传旧字段名并在 API 层转换为 UserOpinionDTO
 * @property {string} [userId] - 用户id
 * @property {number} [userType] - 用户角色 1 SMB 2工程商
 * @property {string} [opinionType] - 反馈类型
 * @property {string} [opinionDetail] - 反馈详情
 * @property {Array<string>} [pic] - 图片（最多 5 张）
 * @property {string} [type] - 问题类型（旧字段，映射到 opinionType）
 * @property {string} [content] - 反馈描述（旧字段，映射到 opinionDetail）
 * @property {Array<string>} [images] - 图片路径列表（旧字段，映射到 pic）
 */

/**
 * @description 将项目旧入参映射到接口所需的 UserOpinionDTO
 * @param {SubmitFeedbackParams} data - 原始入参
 * @returns {UserOpinionDTO}
 */
const normalizeSubmitFeedbackPayload = (data = {}) => {
  /** @type {import('pinia').Store | null} */
  let userStore = null
  try {
    userStore = useUserStore()
  } catch (e) {
    userStore = null
  }

  const userInfo = userStore?.userInfo || {}
  const role = userStore?.role || 'smb'
  const fallbackUserType = role === 'pro' ? 'ENGINEERING' : 'SMB'

  const pics = Array.isArray(data.pics)
    ? data.pics
    : Array.isArray(data.pic)
      ? data.pic
      : Array.isArray(data.images)
        ? data.images
        : []
  const userType =
    data.userType === 2 || data.userType === '2' || data.userType === 'ENGINEERING'
      ? 'ENGINEERING'
      : data.userType === 1 || data.userType === '1' || data.userType === 'SMB'
        ? 'SMB'
        : fallbackUserType

  /** @type {UserOpinionDTO} */
  const payload = {
    userId: data.userId || userInfo.userId || userInfo.id,
    userType,
    opinionType: data.opinionType || data.type,
    opinionDetail: data.opinionDetail || data.content,
    pics: pics.slice(0, 5),
  }

  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
      delete payload[key]
    }
  })

  return payload
}

/**
 * @description 将 contentCategory/tree 接口返回规范为根节点数组
 * @param {unknown} data - 直接数组或含 data 数组的报文
 * @returns {Array}
 */
function normalizeContentCategoryTreeRoots(data) {
  if (Array.isArray(data)) return data
  if (data && Array.isArray(data.data)) return data.data
  return []
}

/**
 * @description 仅保留根节点 categoryName 包含关键字的子树（帮助中心 / 下载中心分流）
 * @param {Array} roots - 根节点列表
 * @param {string} keyword - 如「帮助中心」「下载中心」
 * @returns {Array}
 */
function filterContentCategoryRootsByKeyword(roots, keyword) {
  if (!keyword || !Array.isArray(roots)) return roots || []
  return roots.filter(
    (node) => node && node.categoryName && String(node.categoryName).includes(keyword)
  )
}

/**
 * @description 按 runtimeConfig.public.env 选择帮助中心政策 contentId（常量见 utils/constant.js）
 * @param {number} productionId
 * @param {number} testId
 * @returns {number}
 */
function resolveWebsiteHelpPolicyContentId(productionId, testId) {
  try {
    const runtimeConfig = useRuntimeConfig()
    const env =
      runtimeConfig.public?.env ||
      process.env.NUXT_PUBLIC_ENV ||
      (process.env.NODE_ENV === 'production' ? 'production' : 'development')
    return env === 'production' ? productionId : testId
  } catch {
    return productionId
  }
}

const CONTENT_CENTER_TYPE_TO_TDC = {
  1: 'DOWNLOAD',
  2: 'HELP',
  3: 'ANNOUNCEMENT',
  DOWNLOAD: 'DOWNLOAD',
  HELP: 'HELP',
  ANNOUNCEMENT: 'ANNOUNCEMENT',
}

const CONTENT_CENTER_TYPE_TO_LEGACY = {
  DOWNLOAD: 1,
  HELP: 2,
  ANNOUNCEMENT: 3,
}

const CONTENT_APPLY_STAGE_TO_TDC = {
  1: 'PRE_SALE',
  2: 'AFTER_SALE',
  PRE_SALE: 'PRE_SALE',
  AFTER_SALE: 'AFTER_SALE',
}

const CONTENT_APPLY_STAGE_TO_LEGACY = {
  PRE_SALE: 1,
  AFTER_SALE: 2,
}

const CONTENT_FORM_NAME_MAP = {
  DOCUMENT: '文档',
  VIDEO: '视频',
  IMAGE: '图片',
  CUSTOM: '自定义',
  STATIC_SITE: '产品模拟器',
}

function normalizeArrayValue(value) {
  if (Array.isArray(value))
    return value.filter((item) => item !== undefined && item !== null && item !== '')
  if (value === undefined || value === null || value === '') return []
  return [value]
}

function toTdcContentCenterType(value) {
  return CONTENT_CENTER_TYPE_TO_TDC[value] || CONTENT_CENTER_TYPE_TO_TDC[String(value)] || value
}

function toLegacyContentCenterType(value) {
  return CONTENT_CENTER_TYPE_TO_LEGACY[value] || value
}

function toTdcContentApplyStage(value) {
  return CONTENT_APPLY_STAGE_TO_TDC[value] || CONTENT_APPLY_STAGE_TO_TDC[String(value)] || value
}

function toLegacyContentApplyStage(value) {
  return CONTENT_APPLY_STAGE_TO_LEGACY[value] || value
}

function normalizeStoreContentQuery(params = {}) {
  const {
    applyStages,
    centerType,
    current = 1,
    prodIds,
    secondSceneIds,
    size = 10,
    thirdContentCategoryIds,
    ...rest
  } = params

  const query = {
    ...rest,
    page: current,
    pageSize: size,
    centerType: toTdcContentCenterType(centerType),
    productIds: serializeServiceContentFilterValues(prodIds),
    sceneIds: serializeServiceContentFilterValues(secondSceneIds),
    categoryIds: serializeServiceContentFilterValues(thirdContentCategoryIds),
    applyStages: serializeServiceContentFilterValues(
      normalizeArrayValue(applyStages).map(toTdcContentApplyStage)
    ),
  }

  Object.keys(query).forEach((key) => {
    if (
      query[key] === undefined ||
      query[key] === null ||
      query[key] === '' ||
      (Array.isArray(query[key]) && query[key].length === 0)
    ) {
      delete query[key]
    }
  })

  return query
}

function normalizeStoreContentCategoryNode(node) {
  if (!node || typeof node !== 'object') return node
  const categoryId = node.contentCategoryId ?? node.categoryId ?? node.id
  const categoryName = node.categoryName ?? node.name
  return {
    ...node,
    categoryId,
    categoryName,
    contentCategoryId: categoryId,
    children: Array.isArray(node.children)
      ? node.children.map((child) => normalizeStoreContentCategoryNode(child))
      : node.children,
  }
}

function normalizeStoreContentRecord(item) {
  if (!item || typeof item !== 'object') return item
  const contentId = item.contentId ?? item.id
  const centerTypes = normalizeArrayValue(item.centerTypes).map(toLegacyContentCenterType)
  const applyStages = normalizeArrayValue(item.applyStages).map(toLegacyContentApplyStage)
  const primaryResource = item.primaryResource || {}
  const videoCover = item.videoCover || {}
  const staticSiteUrl = item.staticSiteUrl || ''
  const normalizedPrimary = normalizeContentResource(primaryResource, item)
  const fileUrl =
    item.fileUrl || staticSiteUrl || normalizedPrimary.previewUrl || normalizedPrimary.url || ''
  const coverUrl = resolveContentCoverUrl(videoCover, item)
  const attachments = (item.attachmentInfoList || item.attachments || []).map((attachment) =>
    normalizeContentResource(attachment)
  )

  return {
    ...item,
    applyStage: item.applyStage ?? applyStages[0],
    applyStages,
    attachmentInfoList: attachments,
    centerType: item.centerType ?? centerTypes[0],
    centerTypes,
    content: item.content ?? item.customizeContent ?? item.abstractText ?? '',
    contentFormName: item.contentFormName ?? CONTENT_FORM_NAME_MAP[item.contentForm] ?? '',
    contentId,
    coverUrl,
    createTime: item.createTime ?? item.createdAt,
    description: item.description ?? item.abstractText ?? '',
    filePath: item.filePath || fileUrl,
    fileUrl,
    staticSiteUrl,
    primaryResource: normalizedPrimary,
    videoCover: normalizeContentResource(videoCover),
    applicableProductModels: resolveApplicableProductModels(item),
    updateTime: item.updateTime ?? item.updatedAt,
  }
}

function normalizeStoreContentPageResponse(data) {
  const payload = data?.data || data || {}
  if (!Array.isArray(payload.records) && !Array.isArray(payload.items)) {
    throw new Error('内容列表响应格式异常')
  }
  const records = normalizeArrayValue(payload.records || payload.items).map(
    normalizeStoreContentRecord
  )
  return {
    ...payload,
    current: payload.current ?? payload.page,
    records,
    size: payload.size ?? payload.pageSize,
    total: Number(payload.total) || 0,
  }
}

/**
 * @description 官网模块 API 集合
 */
export const websiteApi = {
  /**
   * @description 获取公司信息用于 SEO（页面加载，失败静默）
   * @returns {Promise<Object>} 公司信息
   */
  getCompanyInfo: () => api.get('/website/company-info', { showError: false }),

  /**
   * @description 获取首页 Banner 列表
   * @returns {Promise<Array>} Banner 列表
   */
  getBanners: () => api.get('/website/banners', { showError: false }),

  /**
   * @description 获取首页精选方案列表（使用 Apifox 小程序找方案接口）
   * @param {Object} params - 查询参数
   * @param {number} params.page - 页码
   * @param {number} params.pageSize - 每页数量
   * @param {string} params.featured - 是否精选，'1' 表示精选
   * @returns {Promise<Object>} 方案列表数据
   * @deprecated 仅保留旧接口兼容；公开官网迁移完成前必须读取 solution-home-content，不得调用 GCB。
   */
  getFeaturedSolutions: (params = {}) => {
    const defaultParams = { current: 1, size: 50, ...params }
    return api.post(
      `/p/solution/list?current=${defaultParams.current}&size=${defaultParams.size}`,
      { ...defaultParams },
      { showError: false }
    )
  },

  /**
   * @description 分页查询案例列表
   * @param {Object} params - 查询参数
   * @param {number} params.current - 当前页码
   * @param {number} params.size - 每页大小
   * @param {string} params.projectName - 项目名称
   * @param {Array<number>} params.firstSceneList - 一级场景id列表
   * @param {Array<number>} params.secondSceneList - 二级场景id列表
   * @param {Array<string>} params.labelList - 标签名称列表
   * @param {number} params.sortField - 排序字段 1收藏量 2点赞量 3浏览量 4 分享量
   * @param {number} params.sortType - 排序方式 1 升序 2 降序
   * @returns {Promise<Object>} 案例列表数据
   * @deprecated 仅保留旧接口兼容；公开官网迁移完成前必须读取 solution-home-content，不得调用 GCB。
   */
  getCaseList: (params = {}, config = {}) => {
    const defaultParams = { current: 1, size: 6, ...params }
    return api.get('/solution/case/page', { params: defaultParams, showError: false, ...config })
  },

  /**
   * @description 获取有效的活动入口（轮播图）
   * @param {Object} params - 查询参数
   * @param {number} params.clientPort - 客户端端口，官网 PC 为 3
   * @param {'TDC'|'IPC'} [params.salesOrganization] - 销售组织，官网默认 TDC
   * @returns {Promise<Object>} 活动入口列表
   */
  getActiveEntries: (params = {}, config = {}) => {
    const defaultParams = { clientPort: 3, salesOrganization: 'TDC', ...params }
    return api.get('/p/store/activityEntryList', {
      showError: false,
      ...config,
      params: defaultParams,
    })
  },

  /**
   * @description 查询方案成功落地故事（公开）
   * @returns {Promise<Array>} 方案成功落地故事列表
   * @deprecated 公开官网已改用 solution-home-content，TDC 原生迁移前不得调用此 GCB 兼容接口。
   */
  getSolutionLandingStories: (params = {}) => {
    return api.post('/p/solution/landing/story', { ...params }, { showError: false })
  },

  /**
   * @description 读取 TDC 从 OSS 托管的方案首页内容配置
   * @returns {Promise<Object>} 方案首页、落地故事及配置详情
   */
  getSolutionHomeContent: (config = {}) =>
    api.get('/store/solution/home-content', { showError: false, ...config }),
  /**
   * @description 获取所有场景分类
   * @param {Object} params - 查询参数
   * @param {boolean} params.needSolutionNum - 是否需要查询方案数量
   * @returns {Promise<Array>} 场景分类列表
   * @deprecated TDC 原生分类迁移完成前，公开官网不得调用此 GCB 兼容接口。
   */
  getSceneCategories: (params = {}, config = {}) => {
    return api.post('/scene-category/all', params, { showError: false, ...config })
  },
  /**
   * @description 获取方案详情
   * @param {string} id - 方案 ID
   * @returns {Promise<Object>} 方案详情
   * @deprecated 公开官网已改用 solution-home-content，TDC 原生迁移前不得调用此 GCB 兼容接口。
   */
  getSolutionDetail: (id, config = {}) =>
    api.get('/p/solution/detail', { params: { solutionId: id }, ...config }),

  /**
   * @description 获取方案列表（可用于首页、列表页、相关方案）
   * @param {Object} data - 查询参数
   * @param {number} [data.current] - 当前页码
   * @param {number} [data.size] - 每页数量
   * @param {Array<number>} [data.secondSceneIds] - 二级场景 ID 列表
   * @param {number} [data.sortField] - 排序字段 1收藏量 2点赞量 3浏览量 4最新发布 5分享量
   * @param {number} [data.sortType] - 排序方式 1 升序 2 降序
   * @param {Object} [config] - Axios 配置（如竞态键）
   * @returns {Promise<Object>} 方案列表分页数据
   * @deprecated 仅保留旧接口兼容；公开官网迁移完成前必须读取 solution-home-content，不得调用 GCB。
   */
  getSolutionList: (data = {}, config = {}) => {
    const { current = 1, size = 10, ...rest } = data
    return api.post(
      `/p/solution/list?current=${current}&size=${size}`,
      { current, size, ...rest },
      { showError: false, ...config }
    )
  },

  /**
   * @description 获取内容分类树形列表
   * @param {Object} [options] - 选项
   * @param {string} [options.rootNameContains] - 根节点 categoryName 需包含的文案；帮助中心传「帮助中心」，下载中心传「下载中心」
   * @returns {Promise<Array>} 分类树形列表
   */
  getContentCategoryTree: (options = {}, config = {}) => {
    const { rootNameContains } = options
    return api
      .get('/p/store/content-category/tree', { showError: false, ...config })
      .then((data) => {
        const roots = normalizeContentCategoryTreeRoots(data).map(normalizeStoreContentCategoryNode)
        return rootNameContains
          ? filterContentCategoryRootsByKeyword(roots, rootNameContains)
          : roots
      })
  },

  /**
   * @description 获取使用场景树形列表（C-end 公开接口）
   * @returns {Promise<Array>} 场景树形列表，节点格式 { id, name, children, grade, parentId, ... }
   */
  getSceneTree: (params = {}, config = {}) => {
    return api
      .get('/store/content-scene/tree', { params, showError: false, ...config })
      .then((data) => {
        if (Array.isArray(data)) return data
        if (data && Array.isArray(data.data)) return data.data
        return []
      })
  },

  /**
   * @description 分页查询内容列表
   * @param {Object} params - 查询参数
   * @param {number} params.current - 当前页码
   * @param {number} params.size - 每页大小
   * @param {number|number[]} params.centerType - 所属中心类型 1 下载中心 2 帮助中心 3 公告中心（查询参数；列表项 records.centerTypes 可能为数组）
   * @param {string} [params.keyword] - 搜索关键词
   * @param {Array<number>} [params.prodIds] - 关联产品ID列表
   * @param {Array<number>} [params.secondSceneIds] - 关联场景ID列表
   * @param {Array<number>} [params.thirdContentCategoryIds] - 内容分类ID列表
   * @param {Array<number>} [params.applyStages] - 适用阶段列表 1 售前 2 售后
   * @param {Object} [config] - Axios 请求配置（如 concurrencyKey）
   * @returns {Promise<Object>} 内容列表分页数据
   */
  getContentList: (params = {}, config = {}) => {
    return api
      .get('/p/store/content/page', {
        params: normalizeStoreContentQuery(params),
        showError: false,
        ...config,
      })
      .then(normalizeStoreContentPageResponse)
  },

  getContentFacets: (params = {}, config = {}) =>
    api
      .get('/p/store/content/facets', {
        params: normalizeStoreContentQuery(params),
        showError: false,
        ...config,
      })
      .then((response) => response?.data || response),

  /**
   * @description 查询已上架、帮助中心下且同时面向全部用户与工程商的产品模拟器静态站点。
   * @param {{ page?: number, pageSize?: number }} params
   * @returns {Promise<Object>} 标准化分页结果
   */
  getProductSimulatorList: (params = {}, config = {}) =>
    api
      .get('/p/store/content/product-simulators', {
        params: {
          page: params.page || 1,
          pageSize: params.pageSize || 10,
        },
        showError: false,
        ...config,
      })
      .then(normalizeStoreContentPageResponse),

  /**
   * @description 获取内容详情
   * @param {string|number} contentId - 内容ID
   * @returns {Promise<Object>} 内容详情数据
   */
  getContentDetail: (contentId, config = {}) =>
    api
      .get('/p/store/content/info', {
        params: { contentId },
        showError: false,
        ...config,
      })
      .then((data) => normalizeStoreContentRecord(data?.data || data)),

  /**
   * @description 更新内容统计（预览量/下载量/分享量/有帮助/无帮助）
   * @param {Object} params - 请求参数
   * @param {string|number} params.contentId - 内容ID
   * @param {number} [params.statType] - 统计类型 1 预览量 2 下载量 3 分享量 4 有帮助 5 无帮助
   * @reason 内容统计属于非阻断型埋点，失败时不应向用户展示全局网络错误提示
   * @returns {Promise<Object>} 操作结果
   */
  updateContentStat: (params) =>
    api.post('/p/store/content/updateStat', params, {
      showError: false,
    }),

  /**
   * @description 提交意见反馈
   * @param {SubmitFeedbackParams} data - 反馈内容（会按 API 文档转换为 UserOpinionDTO）
   * @returns {Promise<Object>} 提交结果
   */
  submitFeedback: (data) => {
    const payload = normalizeSubmitFeedbackPayload(data)
    return api.post('/user/opinion/submit', payload)
  },

  /**
   * @description 获取保修政策（同帮助中心详情：getContentDetail）
   * @returns {Promise<Object>} 内容详情，取值参考 pages/website/service/help/[id].vue
   */
  getWarrantyPolicy: (config = {}) =>
    websiteApi.getContentDetail(
      resolveWebsiteHelpPolicyContentId(
        WARRANTY_POLICY_HELP_CONTENT_ID_PRODUCTION,
        WARRANTY_POLICY_HELP_CONTENT_ID_TEST
      ),
      config
    ),

  /**
   * @description 获取退换货政策（同帮助中心详情：getContentDetail）
   * @returns {Promise<Object>} 内容详情，取值参考 pages/website/service/help/[id].vue
   */
  getExchangePolicy: (config = {}) =>
    websiteApi.getContentDetail(
      resolveWebsiteHelpPolicyContentId(
        EXCHANGE_POLICY_HELP_CONTENT_ID_PRODUCTION,
        EXCHANGE_POLICY_HELP_CONTENT_ID_TEST
      ),
      config
    ),

  /**
   * @description 分页查询收藏的方案列表（分页参数 current/size 走 URL query，其余走 body）
   * @param {Object} params - 查询参数 { solutionName, current, size }
   * @returns {Promise<Object>} 方案列表分页数据
   */
  /** 我的收藏方案（用户相关，失败需提示） */
  getFavoriteSolutions: (params = {}) => {
    const { current, size, ...body } = params
    return api.post('/p/solution/list/favorite', body, { params: { current, size } })
  },

  /**
   * @description 切换方案收藏/点赞状态
   * @param {Object} data - 请求参数 { solutionId, type, actionType }
   * @param {string} data.solutionId - 方案 ID
   * @param {number} data.type - 类型 1 点赞 2 收藏 3 浏览 4 分享
   * @param {number} data.actionType - 操作类型 1 确定 2 取消
   * @returns {Promise<boolean>} 操作结果
   */
  /**
   * @description Record non-blocking solution stats such as view/share.
   * @param {{ solutionId: string|number, type: number, actionType: number }} data - Stat payload.
   * @returns {Promise<boolean>} Operation result.
   * @reason This endpoint also powers favorite actions, but stat failures should stay silent.
   */
  trackSolutionStat: (data) => api.post('/p/solution/favorite', data, { showError: false }),
  toggleSolutionFavorite: (data) => api.post('/p/solution/favorite', data),
}

export { websiteCaseApi } from './case'
